import { Bookmark, BookmarkBorder } from '@mui/icons-material';
import {
  Checkbox,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { useAsyncDebounce } from 'react-table';
import { Search } from './search/search';

export const BasicTable = ({
  columns,
  data,
  enableGlobalFilter,
  enableColumnFilter,
  multiSelect = false,
  singleSelect = false,
  onRowSelected = () => {},
  onRowsSelected = () => {},
}) => {
  const [rowSelection, setRowSelection] = React.useState({});
  const [_columns, setColumns] = React.useState([]);

  if (!columns) throw new Error('columns not defined');

  useEffect(() => {
    singleSelect && onRowSelected(table.getSelectedRowModel().flatRows[0]?.original);
    multiSelect && onRowsSelected(table.getSelectedRowModel().flatRows.map((x) => x.original));
  }, [rowSelection]);

  const initColumns = () => {
    if (multiSelect || singleSelect) {
      setColumns([
        {
          id: 'select',
          header: ({ table }) =>
            multiSelect && (
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllPageRowsSelected(),
                  indeterminate: table.getIsSomePageRowsSelected(),
                  onChange: table.getToggleAllPageRowsSelectedHandler(),
                }}
              />
            ),
          cell: ({ row }) =>
            (singleSelect || multiSelect) && (
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            ),
        },
        ...columns,
      ]);
    } else {
      setColumns([...columns]);
    }
  };

  useEffect(() => {
    if (_columns.length > 0) return;
    initColumns();
  }, [columns]);

  const table = useReactTable({
    data: data,
    columns: _columns,
    state: {
      rowSelection,
    },
    //enableRowSelection: singleSelect || multiSelect, //enable row selection for all rows
    enableMultiRowSelection: multiSelect,
    enableRowSelection: (row) => {
      if (singleSelect || multiSelect) {
        return row.original.selectable;
      }
      return false;
    }, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    //debugTable: true,
    enableGlobalFilter: true,
  });

  const onChange = useAsyncDebounce((value) => table.setGlobalFilter(value || undefined), 200);

  return (
    <React.Fragment>
      <Paper>
        {enableGlobalFilter && (
          <TableRow key="globalsearch" sx={{ my: 3 }}>
            <Search onChange={onChange} />
          </TableRow>
        )}
        <TableContainer sx={{ py: 1 }}>
          <Table size="small">
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableCell key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {enableColumnFilter && header.column.getCanFilter() ? (
                              <Filter column={header.column} table={table} />
                            ) : null}
                          </>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {table.getPageCount() > 1 && (
            <Pagination
              page={table.getState().pagination.pageIndex + 1}
              count={table.getPageCount()}
              disabled={table.getState().pagination.pageSize === 0}
              onChange={(_, page) => table.setPageIndex(page - 1)}
            />
          )}
        </TableContainer>
      </Paper>
    </React.Fragment>
  );
};

function Filter({ column, table }) {
  return (
    <input
      type="text"
      value={column.getFilterValue() ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder={`Search...`}
    />
  );
}

function IndeterminateCheckbox({ indeterminate, ...rest }) {
  return (
    <Checkbox
      icon={<BookmarkBorder />}
      checkedIcon={<Bookmark />}
      checked={rest.checked}
      onChange={rest.onChange}
      disabled={rest.disabled}
      indeterminate={!rest.checked && indeterminate}
      color="secondary"
    />
  );
}
